import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

/** Reasoning control method - how thinking intensity is configured */
export type ReasoningControlType = "effort" | "max_tokens";

/** Effort levels for models using effort-based reasoning control */
export const REASONING_EFFORT_LEVELS = [
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
] as const;
export type ReasoningEffortLevel = (typeof REASONING_EFFORT_LEVELS)[number];

/** LLM model metadata including capabilities */
export type LLMModelMeta = {
  value: string;
  label: string;
  icon: string;
  description: string;
  tags: undefined;
  featured: boolean;
  /** Context window size in tokens (input capacity) */
  contextSize: number;
  /** Maximum output/completion tokens the model supports */
  maxOutputTokens: number;
  /** Whether model supports reasoning/thinking mode */
  supportsReasoning: boolean;
  /** How reasoning intensity is controlled (null if reasoning not supported) */
  reasoningControl: ReasoningControlType | null;
};

// Shared LLM options for Text Completion - used by both schema and UI
export const LLM_OPTIONS: LLMModelMeta[] = [
  {
    value: "openai/gpt-5.2",
    label: "GPT-5.2",
    icon: "openai.svg",
    description:
      "Latest GPT-5 series with adaptive reasoning and stronger agentic performance.",
    tags: undefined,
    featured: true,
    contextSize: 400_000,
    maxOutputTokens: 128_000,
    supportsReasoning: true,
    reasoningControl: "effort",
  },
  {
    value: "openai/gpt-5.1",
    label: "GPT-5.1",
    icon: "openai.svg",
    description:
      "Full-capability GPT-5 successor with adaptive reasoning and natural conversational style.",
    tags: undefined,
    featured: false,
    contextSize: 400_000,
    maxOutputTokens: 128_000,
    supportsReasoning: true,
    reasoningControl: "effort",
  },
  {
    value: "openai/gpt-5-chat",
    label: "GPT-5 Chat",
    icon: "openai.svg",
    description: "GPT-5 variant tuned for fast, natural conversational chat.",
    tags: undefined,
    featured: false,
    contextSize: 128_000,
    maxOutputTokens: 16_384,
    supportsReasoning: true,
    reasoningControl: "effort",
  },
  {
    value: "google/gemini-3-flash-preview",
    label: "Gemini 3 Flash",
    icon: "gemini-color.svg",
    description:
      "Fast thinking model for agentic workflows with near Pro-level reasoning.",
    tags: undefined,
    featured: true,
    contextSize: 1_048_576,
    maxOutputTokens: 65_535,
    supportsReasoning: true,
    reasoningControl: "effort",
  },
  {
    value: "anthropic/claude-opus-4.5",
    label: "Claude Opus 4.5",
    icon: "claude-logo.svg",
    description:
      "Anthropic's frontier model for complex engineering, agentic workflows, and multi-step reasoning.",
    tags: undefined,
    featured: true,
    contextSize: 200_000,
    maxOutputTokens: 32_000,
    supportsReasoning: true,
    reasoningControl: "effort",
  },
  {
    value: "anthropic/claude-sonnet-4.5",
    label: "Claude Sonnet 4.5",
    icon: "claude-logo.svg",
    description:
      "Anthropic's balanced model for deep reasoning, writing, and tools.",
    tags: undefined,
    featured: true,
    contextSize: 1_000_000,
    maxOutputTokens: 64_000,
    supportsReasoning: true,
    reasoningControl: "effort",
  },
  {
    value: "anthropic/claude-sonnet-4.6",
    label: "Claude Sonnet 4.6",
    icon: "claude-logo.svg",
    description:
      "Latest Claude Sonnet with improved reasoning and tool use capabilities.",
    tags: undefined,
    featured: false,
    contextSize: 1_000_000,
    maxOutputTokens: 64_000,
    supportsReasoning: true,
    reasoningControl: "effort",
  },
  {
    value: "x-ai/grok-4",
    label: "Grok-4",
    icon: "grok.svg",
    description:
      "xAI's top model, strong on reasoning, coding, and current-events context.",
    tags: undefined,
    featured: true,
    contextSize: 256_000,
    maxOutputTokens: 32_000,
    supportsReasoning: true,
    reasoningControl: "effort",
  },
  {
    value: "x-ai/grok-4.3",
    label: "Grok 4.3",
    icon: "grok.svg",
    description:
      "xAI's reasoning model with 1M context and configurable effort levels for agentic, instruction-following workloads.",
    tags: undefined,
    featured: true,
    contextSize: 1_000_000,
    maxOutputTokens: 32_000,
    supportsReasoning: true,
    reasoningControl: "effort",
  },
];

export const LLM_VALUES = LLM_OPTIONS.map((opt) => opt.value) as [
  string,
  ...string[],
];

/** Type representing valid LLM model identifiers from LLM_OPTIONS */
export type VariationLlmModel = LLMModelMeta["value"];

/** Set for O(1) validation of LLM model values */
export const LLM_VALUES_SET = new Set<string>(LLM_VALUES);

/** Type guard to check if a string is a valid VariationLlmModel */
export function checkIsValidVariationLlm(
  value: unknown,
): value is VariationLlmModel {
  return typeof value === "string" && LLM_VALUES_SET.has(value);
}

/** Map for O(1) lookup of model metadata by value */
const LLM_OPTIONS_MAP = new Map<string, LLMModelMeta>(
  LLM_OPTIONS.map((opt) => [opt.value, opt]),
);

/** Get model metadata by model value identifier */
export function getLLMModelMeta(value: string): LLMModelMeta | undefined {
  return LLM_OPTIONS_MAP.get(value);
}

// Retired ids mapped to their current replacements. Keep in sync with the
// Zod preprocess below so the UI and the runtime resolve stored blueprint
// values the same way. Remove after 2026-06-15 once the deprecated alias
// is no longer in active use.
const LLM_MODEL_ALIASES: Record<string, string> = {
  "x-ai/grok-4-fast": "x-ai/grok-4.3",
};

/** Resolve a stored LLM model id, applying back-compat aliases. */
export function resolveLlmModelId(value: string): string {
  return LLM_MODEL_ALIASES[value] ?? value;
}

// Up to 4 optional media inputs (images or videos) for vision models
// Group is "images" because the LLM always receives images (videos are converted to composite grids)
const MEDIA_ATTACHMENT_SLOTS = Array.from({ length: 4 }, (_, index) => ({
  type: "media" as const,
  allowedFormats: [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
    "video/mp4",
    "video/webm",
    "video/quicktime",
  ],
  label: `Media ${index + 1}`,
  slot: `media_${index + 1}`,
  group: "images",
  required: false,
}));

const openrouterCompletionParams = z
  .object({
    // Backwards-compat alias: the retired "x-ai/grok-4-fast" ID transparently
    // resolves to "x-ai/grok-4.3" so existing scripted callers keep working
    // through the transition. Shares LLM_MODEL_ALIASES with the UI helper
    // above so the editor sees the same resolved id at read time.
    llm: z
      .preprocess(
        (v) => (typeof v === "string" ? resolveLlmModelId(v) : v),
        z.enum(LLM_VALUES),
      )
      .optional()
      .default("x-ai/grok-4.3"),
    prompt: z.string().default(""),
    systemPrompt: z.string().optional().default(""),
    attachments: z.array(attachment).optional(),
    // Reasoning/thinking settings
    reasoningEnabled: z.boolean().optional(),
    reasoningEffort: z
      .enum(["minimal", "low", "medium", "high", "xhigh"])
      .optional(),
    reasoningMaxTokens: z.number().min(1024).max(32000).optional(),
    // Max output tokens (overall response limit, effort is applied as % of this)
    maxOutputTokens: z.number().min(1024).max(128000).optional(),
  })
  .strip();

type OpenRouterCompletionParams = z.infer<typeof openrouterCompletionParams>;

export const openrouterCompletion = defineModel({
  key: "openrouter-completion",
  stepType: "text",
  spec: {
    label: "Text Completion",
    description:
      "Generate text completions using LLM. Supports optional image and video inputs for vision models.",
    icon: "text-icon.svg",
    tags: ["text-gen", "llm", "vision", "video-analysis"],
    features: { prompts: true, attachments: true } as const,
    options: {
      attachments: MEDIA_ATTACHMENT_SLOTS,
    },
    params: openrouterCompletionParams,
  },
  providers: [
    {
      provider: "openrouter",
      modelId: "openrouter-completion",
      estimateCost(_params: OpenRouterCompletionParams) {
        // Rough estimate - adjust based on actual pricing
        // Multimodal requests may cost more but we use a baseline estimate
        return 0.01 * 1_000_000 * (1 + BRAND_MARKUP);
      },
      toPayload(params: OpenRouterCompletionParams) {
        // Extract media attachments and determine which are videos
        // Slots can be individual (media_1, media_2, image_1) or group handles (group:images)
        const mediaAttachments =
          params.attachments?.filter(
            (file) =>
              file.slot.startsWith("media_") ||
              file.slot.startsWith("image_") ||
              file.slot === "group:images",
          ) ?? [];

        // Separate video and image attachments
        const videoAttachments = mediaAttachments.filter(
          (file) => file.mediaKind === "video",
        );
        const imageAttachments = mediaAttachments.filter(
          (file) => file.mediaKind !== "video",
        );

        // For videos, use composite grid URL if available
        // Videos without composite grids are skipped (they need to be re-processed)
        const videoImageUrls = videoAttachments
          .map((file) => file.compositeGridUrl)
          .filter((url): url is string => Boolean(url));

        // For images, use the regular URL
        const imageUrls = imageAttachments
          .map((file) => file.data ?? file.url)
          .filter((url): url is string => Boolean(url));

        // Combine all image URLs (composite grids from videos + regular images)
        const allImageUrls = [...videoImageUrls, ...imageUrls];

        // Add system prompt for video analysis - instruct the AI to respond naturally
        // as if watching the video, without revealing the frame extraction technique
        let finalSystemPrompt = params.systemPrompt ?? "";
        if (videoImageUrls.length > 0) {
          const videoSystemInstruction = `You are analyzing video content. Respond naturally as if you watched the video directly. Never mention technical details about how you process or view the video.

If the user has provided additional instructions below, follow them - they take priority and may override these defaults.`;
          finalSystemPrompt = finalSystemPrompt
            ? `${videoSystemInstruction}\n\n---\nUser instructions:\n${finalSystemPrompt}`
            : videoSystemInstruction;
        }

        return {
          prompt: params.prompt,
          llm: params.llm,
          systemPrompt: finalSystemPrompt,
          ...(allImageUrls.length > 0 ? { imageUrls: allImageUrls } : {}),
          // Reasoning settings
          ...(params.reasoningEnabled !== undefined && {
            reasoningEnabled: params.reasoningEnabled,
          }),
          ...(params.reasoningEffort && {
            reasoningEffort: params.reasoningEffort,
          }),
          ...(params.reasoningMaxTokens && {
            reasoningMaxTokens: params.reasoningMaxTokens,
          }),
          ...(params.maxOutputTokens && {
            maxOutputTokens: params.maxOutputTokens,
          }),
        };
      },
    },
  ],
});
